import withWidth, { WithWidth } from "@material-ui/core/withWidth";
import { observer } from "mobx-react";
import * as React from "react";
import { LayoutChildProps } from "../config/Layout";
import ButtonLink from "../coreui/ButtonLink";
import FormData from "../coreui/FormData";
import MenuItem from "../coreui/MenuItem";
import Api, { AccessLevel } from "./Api";
import ApiButton from "./ApiButton";
import { MenuChild } from "./MenuButton";
import { MenuItemProps } from "./MenuItem";
import { ToolbarChildProps } from "./Toolbar";

interface Props {
  dataId: string;
  displayType: {
    lg: "Link" | "Small Button" | "Medium Button" | "Large Button";
    md: "Link" | "Small Button" | "Medium Button" | "Large Button";
    sm: "Link" | "Small Button" | "Medium Button" | "Large Button";
    xl: "Link" | "Small Button" | "Medium Button" | "Large Button";
    xs: "Link" | "Small Button" | "Medium Button" | "Large Button";
  };
  iconName: string;
  label?: string;
  linkType: "External" | "Internal";
  name: string;
  propagated?: ToolbarChildProps & LayoutChildProps & MenuChild;
}

interface RuntimeProperties {
  accessLevel: AccessLevel;
  anchorText: string;
  url: string;
}

@observer
export class ManualLink extends React.Component<Props & WithWidth> {
  public static renderMenuItem(props: MenuItemProps): JSX.Element {
    const { config, runtime, ...otherProps } = props;
    const configProps = (config as unknown) as Props;
    const runtimeProps = runtime as RuntimeProperties;

    let url: string | undefined = undefined;
    if (runtimeProps.accessLevel >= AccessLevel.actionable) {
      url = runtimeProps.url;
    }

    const onClick = (): void => {
      if (runtimeProps.accessLevel >= AccessLevel.actionable) {
        configProps.propagated!.onItemClicked!();
      }
    };

    return (
      <MenuItem
        disabled={runtimeProps.accessLevel === AccessLevel.disabled}
        href={url}
        iconName={configProps.iconName}
        indent={props.config.propagated ? props.config.propagated.indent : 0}
        onClick={onClick}
        target={configProps.linkType === "Internal" ? "_self" : "_blank"}
        {...otherProps}
      >
        {runtimeProps.anchorText}
      </MenuItem>
    );
  }

  public render(): React.ReactNode {
    const _props = { ...this.props };
    const runtimeProperties = Api.getWidgetProperties(
      _props
    ) as RuntimeProperties;

    const tabIndex: number =
      _props.propagated &&
      (_props.propagated.hasParentToolbar || _props.propagated.isInPageFooter)
        ? -1
        : 0;

    if (_props.displayType[this.props.width] === "Link") {
      if (!runtimeProperties) {
        return null;
      }

      if (runtimeProperties.accessLevel === AccessLevel.hidden) {
        return null;
      }

      let result: React.ReactNode = null;
      const link = (
        <ButtonLink
          disabled={runtimeProperties.accessLevel <= AccessLevel.readOnly}
          iconName={_props.iconName}
          href={runtimeProperties.url}
          rel={_props.linkType === "External" ? "noopener" : undefined}
          tabIndex={tabIndex}
          target={_props.linkType === "External" ? "_blank" : "_self"}
        >
          {runtimeProperties.anchorText}
        </ButtonLink>
      );

      if (_props.label) {
        result = <FormData {..._props}>{link}</FormData>;
      } else {
        result = link;
      }

      return result;
    }

    let displayType: "large" | "medium" | "small" | undefined = undefined;
    if (this.props.width in _props.displayType) {
      switch (_props.displayType[this.props.width]) {
        case "Small Button":
          displayType = "small";
          break;
        case "Medium Button":
          displayType = "medium";
          break;
        case "Large Button":
          displayType = "large";
          break;
        default:
          throw new Error(
            "Unknown manual link display type" +
              `${_props.displayType[this.props.width]}`
          );
      }
    }

    return (
      <ApiButton
        buttonColor="Default"
        dataId={_props.dataId}
        disabledHelpText=""
        href={runtimeProperties.url}
        iconName={_props.iconName}
        isIconOnly={false}
        label={runtimeProperties.anchorText}
        name={_props.name}
        size={displayType}
        tabIndex={tabIndex}
        target={_props.linkType === "Internal" ? "_self" : "_blank"}
      />
    );
  }
}

export default withWidth()(ManualLink);
